import { useState } from 'react';
import type { FormEvent } from 'react';
import type { User } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Modal } from './Modal';
import { AlertCircle, Loader2 } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import type { BhcsWaiverMetadata } from '../types/waiver';
import { readWaiverContacts } from '../types/waiver';

interface ContactFields {
  name: string;
  email: string;
  phone: string;
  relationship: string;
}

interface PartialContact {
  name?: string;
  email?: string;
  phone?: string;
  relationship?: string;
}

const EMPTY_CONTACT: ContactFields = { name: '', email: '', phone: '', relationship: '' };

function toFields(contact: PartialContact | undefined): ContactFields {
  if (!contact) return { ...EMPTY_CONTACT };
  return {
    name: contact.name ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    relationship: contact.relationship ?? '',
  };
}

interface StudentContactsEditorProps {
  studentId: string;
  studentName: string;
  // Pass the raw students.metadata.bhcs_waiver value — handles signed,
  // opt-out, and missing-key rows without the caller needing to branch.
  waiver: BhcsWaiverMetadata | null | undefined;
  onClose: () => void;
  // Called after a successful save so the caller can refresh its own list/cache.
  onSaved: () => void;
  // Audit trail. Tracking lives here rather than in the two callers so the
  // parent and admin surfaces emit an identical event for an identical edit —
  // this record is the provenance of a legally meaningful contact sheet.
  user: User | null;
  userType: 'parent' | 'admin';
}

// Which sections actually changed, for the audit event. Field-level values are
// deliberately NOT recorded: app_events is a broad-read audit log and these are
// guardian phone numbers and emails.
function changedSections(before: ContactFields, after: ContactFields): boolean {
  return before.name.trim() !== after.name.trim()
    || before.email.trim() !== after.email.trim()
    || before.phone.trim() !== after.phone.trim()
    || before.relationship.trim() !== after.relationship.trim();
}

export function StudentContactsEditor({
  studentId, studentName, waiver, onClose, onSaved, user, userType,
}: StudentContactsEditorProps) {
  const contacts = readWaiverContacts(waiver);
  const initial = {
    parent1: toFields(contacts.parent1),
    parent2: toFields(contacts.parent2),
    emergencyContact: toFields(contacts.emergencyContact),
    hadParent2: !!contacts.parent2,
  };

  const [parent1, setParent1] = useState<ContactFields>(() => toFields(contacts.parent1));
  const [parent2, setParent2] = useState<ContactFields>(() => toFields(contacts.parent2));
  const [showParent2, setShowParent2] = useState(() => !!contacts.parent2);
  const [emergencyContact, setEmergencyContact] = useState<ContactFields>(() => toFields(contacts.emergencyContact));
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmedParent1Name = parent1.name.trim();
      const trimmedEmergencyName = emergencyContact.name.trim();
      if (!trimmedParent1Name) throw new Error('Guardian 1 name is required');
      if (!trimmedEmergencyName) throw new Error('Emergency contact name is required');

      const { error } = await supabase.rpc('update_student_waiver_contacts', {
        p_student_id: studentId,
        p_parent1_name: trimmedParent1Name,
        p_parent1_email: parent1.email.trim() || null,
        p_parent1_phone: parent1.phone.trim() || null,
        p_parent1_relationship: parent1.relationship.trim() || null,
        p_parent2_name: showParent2 ? (parent2.name.trim() || null) : null,
        p_parent2_email: showParent2 ? (parent2.email.trim() || null) : null,
        p_parent2_phone: showParent2 ? (parent2.phone.trim() || null) : null,
        p_parent2_relationship: showParent2 ? (parent2.relationship.trim() || null) : null,
        p_emergency_name: trimmedEmergencyName,
        p_emergency_email: emergencyContact.email.trim() || null,
        p_emergency_phone: emergencyContact.phone.trim() || null,
        p_emergency_relationship: emergencyContact.relationship.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      const keepsParent2 = showParent2 && !!parent2.name.trim();
      trackEvent({
        eventType: 'waiver_contacts_update',
        user,
        userType,
        entityType: 'student',
        entityId: studentId,
        metadata: {
          student_name: studentName,
          changed: [
            changedSections(initial.parent1, parent1) ? 'parent1' : null,
            changedSections(initial.parent2, parent2) ? 'parent2' : null,
            changedSections(initial.emergencyContact, emergencyContact) ? 'emergencyContact' : null,
          ].filter(Boolean),
          parent2_action: initial.hadParent2 === keepsParent2
            ? 'unchanged'
            : (keepsParent2 ? 'added' : 'removed'),
        },
      });
      onSaved();
      onClose();
    },
    onError: (err: unknown) => {
      const message = err instanceof Error
        ? err.message
        : (typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message?: unknown }).message === 'string')
          ? (err as { message: string }).message
          : 'Failed to save contacts';
      setFormError(message);
      // Failed attempts are tracked too: a denial here means the RPC's
      // authorization check rejected the caller, which is worth seeing.
      trackEvent({
        eventType: 'waiver_contacts_update',
        user,
        userType,
        entityType: 'student',
        entityId: studentId,
        success: false,
        errorMessage: message,
        metadata: { student_name: studentName },
      });
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    mutation.mutate();
  };

  const renderFieldset = (
    label: string,
    values: ContactFields,
    setValues: (v: ContactFields) => void,
    idPrefix: string,
    required: boolean,
  ) => (
    <fieldset className="space-y-3">
      <legend className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium text-gray-700 mb-1">
            Name{required && ' *'}
          </label>
          <input
            id={`${idPrefix}-name`}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={values.name}
            onChange={e => setValues({ ...values, name: e.target.value })}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-relationship`} className="block text-sm font-medium text-gray-700 mb-1">
            Relationship
          </label>
          <input
            id={`${idPrefix}-relationship`}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={values.relationship}
            onChange={e => setValues({ ...values, relationship: e.target.value })}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-phone`} className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            id={`${idPrefix}-phone`}
            type="tel"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={values.phone}
            onChange={e => setValues({ ...values, phone: e.target.value })}
            disabled={mutation.isPending}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`${idPrefix}-email`} className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={values.email}
            onChange={e => setValues({ ...values, email: e.target.value })}
            disabled={mutation.isPending}
          />
        </div>
      </div>
    </fieldset>
  );

  return (
    <Modal isOpen onClose={onClose} title={`Update contacts — ${studentName}`}>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="student-contacts-editor">
        <p className="text-sm text-gray-500">
          Guardian 1 is shown "as signed" on the waiver and is stored separately from your account
          profile — update your own name and phone from Account settings.
        </p>

        {renderFieldset('Guardian 1', parent1, setParent1, 'g1', true)}

        <div>
          {!showParent2 ? (
            <button
              type="button"
              onClick={() => setShowParent2(true)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              disabled={mutation.isPending}
            >
              + Add guardian 2
            </button>
          ) : (
            <>
              {renderFieldset('Guardian 2', parent2, setParent2, 'g2', false)}
              <button
                type="button"
                onClick={() => { setShowParent2(false); setParent2({ ...EMPTY_CONTACT }); }}
                className="mt-2 text-xs text-red-600 hover:text-red-800"
                disabled={mutation.isPending}
              >
                Remove guardian 2
              </button>
            </>
          )}
        </div>

        {renderFieldset('Emergency Contact', emergencyContact, setEmergencyContact, 'ec', true)}

        {formError && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {formError}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save contacts
          </button>
        </div>
      </form>
    </Modal>
  );
}
